import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { IProduct } from 'src/types'
import { aggregateImpacts } from 'src/utils/impact.util'
import { useAccountImpacts, useProductImpacts, useProductJourney } from '../fetch'
import { DEFAULT_LANGUAGE, PATHS, ROUTES } from 'src/constants'

type UseNavigationAvailabilityParams = {
  product?: IProduct | null
  productId?: string | null
  simpleProductId?: string | number | null
  basePath?: string | null
  language?: string
}

type NavigationPaths = {
  product?: string
  journey?: string
  impacts?: string
  account?: string
}

export const useNavigationAvailability = ({
  product,
  productId,
  simpleProductId,
  basePath,
  language,
}: UseNavigationAvailabilityParams) => {
  const [, i18n] = useTranslation()
  const resolvedLanguage = language || i18n.language

  const resolvedProductId = product?.id || productId || ''
  const resolvedSimpleId =
    typeof simpleProductId === 'number'
      ? simpleProductId.toString()
      : simpleProductId ?? (product?.simple_id != null ? String(product.simple_id) : undefined)
  const impactsProductId = resolvedProductId || undefined

  const normalizedBasePath =
    basePath && basePath !== '/'
      ? basePath.replace(/\/+$/, '')
      : basePath === '/'
        ? '/'
        : undefined

  const buildPaths = (): NavigationPaths => {
    const productPath = normalizedBasePath
    const journeyPath = normalizedBasePath ? `${normalizedBasePath}/${ROUTES.JOURNEY}` : undefined
    const impactsPath = normalizedBasePath ? `${normalizedBasePath}/${PATHS.IMPACTS}` : undefined
    const accountPath =
      product?.account_simple_id != null
        ? resolvedLanguage === DEFAULT_LANGUAGE
          ? `/${ROUTES.ACCOUNT}/${product.account_simple_id}`
          : `/${resolvedLanguage}/${ROUTES.ACCOUNT}/${product.account_simple_id}`
        : undefined

    return {
      product: productPath,
      journey: journeyPath,
      impacts: impactsPath,
      account: accountPath,
    }
  }

  const {
    data: journeyData,
    isLoading: isJourneyLoading,
  } = useProductJourney(resolvedProductId, resolvedLanguage, resolvedSimpleId)

  const journeySteps =
    journeyData?.product?.journey?.steps ?? product?.journey?.steps ?? []
  const hasJourney = journeySteps.length > 0

  const {
    data: productStepsImpacts,
    isLoading: isProductImpactsLoading,
  } = useProductImpacts(resolvedProductId, resolvedLanguage)

  const {
    data: accountImpacts,
    isLoading: isAccountImpactsLoading,
  } = useAccountImpacts(resolvedLanguage, undefined, impactsProductId)

  const productImpacts = journeyData?.product?.impacts ?? product?.impacts ?? []

  const aggregatedImpacts = useMemo(
    () =>
      aggregateImpacts({
        accountImpacts,
        productImpacts,
        stepsImpacts: productStepsImpacts?.impacts,
      }),
    [accountImpacts, productImpacts, productStepsImpacts?.impacts],
  )

  const resolvedPaths = buildPaths()

  return {
    hasJourney,
    hasImpacts: aggregatedImpacts.isImpactsPage,
    impactsToShow: aggregatedImpacts.impactsToShow,
    isLoading: isJourneyLoading || isProductImpactsLoading || isAccountImpactsLoading,
    product: journeyData?.product ?? product ?? null,
    paths: resolvedPaths,
    simpleProductId: resolvedSimpleId,
  }
}

